'use client';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Flex,
  LeftNavItem,
  Select,
  Separator,
  Text,
  Pill,
  Shimmer,
  Card,
  CardContent,
  Button,
} from '@/_components';
import { NotionProductUpdate } from '@/_api/notion';
import styles from './ChangeLogDialog.module.css';

export const ChangeLogDialog: FC<{
  isOpen: boolean;
  onClose: () => void;
  hasNewUpdates: boolean;
  setHasNewUpdates: (_value: boolean) => void;
  changelogData: NotionProductUpdate[];
  isLoading: boolean;
  onRefresh: () => void;
  capturedLastReviewedAt: string | null;
}> = ({
  isOpen,
  onClose,
  changelogData,
  isLoading,
  onRefresh,
  capturedLastReviewedAt,
}) => {
  const dataMapped = changelogData.map((item: NotionProductUpdate) => {
    const row = item.properties;
    return {
      title: row.update_title,
      description: row.customer_facing_description || row.engineer_description,
      category: row.category,
      quarter: row.quarter,
      display: row.external_or_internal === 'External',
      link: row.link,
      help_center_link: row.help_center_link,
      createdAt: item.created_time,
      lastEditedTime: item.last_edited_time,
    };
  });

  const quarterOptions = Array.from(
    new Set(
      dataMapped
        .filter((item) => !!item.quarter)
        .map((item) => item.quarter)
        .flat(),
    ),
  )
    .sort()
    .map((quarter) => ({
      name: quarter,
      value: quarter,
    }));

  const [selectedQuarter, setSelectedQuarter] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const navItems = useMemo(() => {
    // check if an item is new
    const checkIsNew = (lastEditedTime?: string): boolean => {
      if (!lastEditedTime) return false;

      let userLastReviewed = null;
      if (capturedLastReviewedAt) {
        try {
          const parsedDate = new Date(capturedLastReviewedAt);
          if (!isNaN(parsedDate.getTime())) {
            userLastReviewed = parsedDate;
          }
        } catch (error) {
          console.warn(
            'Error parsing captured change_log_last_reviewed_at:',
            error,
          );
        }
      }

      const editedTime = new Date(lastEditedTime);
      if (isNaN(editedTime.getTime())) return false;

      if (!userLastReviewed) {
        return true;
      } else if (userLastReviewed && !isNaN(userLastReviewed.getTime())) {
        return editedTime.getTime() > userLastReviewed.getTime();
      }
      return false;
    };

    const categoryItems = Array.from(
      new Set(dataMapped.map((item) => item.category).flat()),
    )
      .sort()
      .map((category) => {
        // Check if this category has any new items
        const hasNewItems = dataMapped.some(
          (item) =>
            item.category.includes(category) &&
            item.display &&
            checkIsNew(item.lastEditedTime),
        );

        return {
          name: category,
          isActive: selectedCategory && selectedCategory === category,
          hasNewItems,
        };
      });

    // Add "All" category at the beginning
    const hasAnyNewItems = dataMapped.some(
      (item) => item.display && checkIsNew(item.lastEditedTime),
    );

    return [
      {
        name: 'All',
        isActive: selectedCategory === 'All',
        hasNewItems: hasAnyNewItems,
      },
      ...categoryItems,
    ];
  }, [dataMapped, selectedCategory, capturedLastReviewedAt]);

  const isNew = useCallback(
    (lastEditedTime?: string): boolean => {
      if (!lastEditedTime) return false;

      // Handle date parsing more robustly using captured timestamp
      let userLastReviewed = null;
      if (capturedLastReviewedAt) {
        try {
          const parsedDate = new Date(capturedLastReviewedAt);
          if (!isNaN(parsedDate.getTime())) {
            userLastReviewed = parsedDate;
          } else {
            console.warn(
              'Invalid date format for captured change_log_last_reviewed_at:',
              capturedLastReviewedAt,
            );
          }
        } catch (error) {
          console.warn(
            'Error parsing captured change_log_last_reviewed_at:',
            error,
          );
        }
      }

      const editedTime = new Date(lastEditedTime);
      if (isNaN(editedTime.getTime())) return false;

      // Show "new" if the item is newer than user's last review
      if (!userLastReviewed) {
        // No previous review, show as new
        return true;
      } else if (userLastReviewed && !isNaN(userLastReviewed.getTime())) {
        // Valid date, compare timestamps
        return editedTime.getTime() > userLastReviewed.getTime();
      }

      // Invalid date format, but user has a timestamp, assume they've reviewed
      return false;
    },
    [capturedLastReviewedAt],
  );

  const selectedData = useMemo(() => {
    const isAllCategory = selectedCategory === 'All';

    return dataMapped
      .filter(
        (item) =>
          item.display &&
          item.quarter === selectedQuarter &&
          (isAllCategory || item.category.includes(selectedCategory)),
      )
      .sort((a, b) => {
        if (isAllCategory) {
          // For "All" category: sort by new items first, then by last_edited_time
          const aIsNew = isNew(a.lastEditedTime);
          const bIsNew = isNew(b.lastEditedTime);

          if (aIsNew && !bIsNew) return -1;
          if (!aIsNew && bIsNew) return 1;

          // If both are new or both are not new, sort by last_edited_time
          const aTime = new Date(a.lastEditedTime || 0).getTime();
          const bTime = new Date(b.lastEditedTime || 0).getTime();
          return bTime - aTime;
        } else {
          // For other categories: sort by last_edited_time, newest first
          const aTime = new Date(a.lastEditedTime || 0).getTime();
          const bTime = new Date(b.lastEditedTime || 0).getTime();
          return bTime - aTime;
        }
      });
  }, [dataMapped, selectedQuarter, selectedCategory, isNew]);

  useEffect(() => {
    if (
      !quarterOptions.length ||
      !navItems.length ||
      !!selectedQuarter ||
      !!selectedCategory
    ) {
      return;
    }

    setSelectedQuarter(quarterOptions?.[0]?.value || '');
    setSelectedCategory('All');
  }, [
    quarterOptions,
    navItems,
    selectedQuarter,
    selectedCategory,
    setSelectedQuarter,
    setSelectedCategory,
  ]);

  const loading = () => {
    return (
      <Flex isCentered={true}>
        <Shimmer isFullWidth={true} width={32} height={64} />
      </Flex>
    );
  };

  const Placeholder = () => {
    const isValid = selectedQuarter && selectedCategory;
    const hasNoData = isValid && !selectedData.length;

    const placeholderText = !isValid
      ? 'Please select a quarter and category to see the updates.'
      : hasNoData
        ? 'No updates found for the selected quarter and category.'
        : '';
    return (
      <Flex gap={8} isCentered={true} isFullWidth={true}>
        <Text type="small" color="disabled">
          {placeholderText}
        </Text>
      </Flex>
    );
  };

  const headerButtons = (
    <Button
      value="Refresh"
      iconLeft="refresh"
      isIconOnly={true}
      withTooltip={true}
      intent="ghost"
      size="small"
      onClick={onRefresh}
      isLoading={isLoading}
    />
  );

  return (
    <Dialog
      className={styles.dialog}
      isOpen={isOpen}
      onClose={onClose}
      title="What's New"
      isWide={true}
      headerButtons={headerButtons}
    >
      <Dialog.Content>
        {isLoading ? (
          loading()
        ) : (
          <Flex gap={8}>
            <Flex gap={4}>
              <Flex isColumn={true} gap={6} className={styles.navItems}>
                <Select
                  isFullWidth={true}
                  label="Quarter"
                  placeholder="Select Quarter"
                  isLabelHidden={true}
                  items={quarterOptions}
                  onChange={(e) => setSelectedQuarter(e.target.value)}
                  selectedValue={selectedQuarter}
                />
                {navItems.map((item, key) => (
                  <LeftNavItem
                    name={item.name}
                    key={key}
                    isActive={item.isActive || false}
                    className={
                      item.name === 'All' && item.hasNewItems
                        ? styles.categoryHasNewItems
                        : styles.categoryItem
                    }
                    isTextOnly={true}
                    onClick={() => setSelectedCategory(item.name)}
                  />
                ))}
              </Flex>
              <Separator isVertical={true} />
            </Flex>
            {!!selectedData.length ? (
              <Flex isColumn={true} gap={6} isFullWidth={true}>
                {!!selectedData.length && (
                  <Text className={styles.banner} type="small">
                    {selectedQuarter}/ {selectedCategory} /{' '}
                    {selectedData.length} updates
                  </Text>
                )}
                <div className={styles.cardsGrid}>
                  {selectedData.map((item, key) => (
                    <Card
                      key={`update-${key}-${item.createdAt}-${item.title}`}
                      padding={4}
                      className={styles.card}
                    >
                      <CardContent>
                        <Flex isColumn={true} gap={8} padding={4}>
                          <Flex isBetween={true} gap={2}>
                            <Text type="small" fontWeight="medium">
                              {item.title}
                            </Text>
                            {isNew(item.lastEditedTime) && (
                              <Pill intent="accent" size="small" label="New" />
                            )}
                          </Flex>
                          <Text type="small" color="secondary">
                            {item.description}
                          </Text>
                          {!!item.help_center_link && (
                            <a
                              href={item.help_center_link as string}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Text type="small" color="blue" underline={true}>
                                Learn more →
                              </Text>
                            </a>
                          )}
                        </Flex>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </Flex>
            ) : (
              <Placeholder />
            )}
          </Flex>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
